import React, { useEffect, useRef, useState } from "react";
import ReactDOM from 'react-dom';
import { useTranslation } from "react-i18next";
import axiosInstance from "../components/OrganisationSearch/services/api";
import { AsyncPaginate } from "react-select-async-paginate";
import PropTypes from "prop-types";
import "../i18n";
import { fetchPostalCode, getCoordinatesFromPostalCode, getLKey } from "../components/OrganisationSearch/utils/helpers";
import { customStyles } from "../components/OrganisationSearch/components/PostalCode/styles";

const IMAGE = {
    "respite_location": "ic_repit.svg",
    "information": "ic_informations.svg",
    "trainings": "ic_activite_formation.svg",
    "support_groups": "ic_entraide.svg",
    "administratives": "ic_juridique.svg",
    "home_assists": "ic_aide_domicile.svg",
    "transportations": "ic_transport.svg",
    "accommodations": "ic_hebergement.svg",

};

const fetchServices = async () => {
    const url = `/api/services/`;
    const response = await axiosInstance.get(url);
    return response.data;
};
const fetchSubServices = async (service) => {
    const url = `/api/search/related-field-model/?field=${ service }&document=Resource&page_size=100&page=1`;
    const response = await axiosInstance.get(url);
    return response.data;
};

// eslint-disable-next-line react/prop-types
const PostalCode = ({ setPostalCode, setShowPostalCodeError, showPostalCodeError }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const currentLanguage = window.languageCode;

    const loadOptions = async (inputValue = "", prevOptions) => {
        try {
            const response = await axiosInstance.get(
                "/api/postal-code/?q=" + inputValue
            );
            const data = response.data;

            const newOptions = data.results.map((item) => ({
                ...item,
                value: item.id,
                label: item.city + "(" + item.code + `)`,
            }));
            return {
                options: newOptions,
            };
        } catch (error) {
            console.log(prevOptions);
            console.error("An error occurred while fetching data:", error);
            return [];
        }
    };

    const handleSelectedValue = async (selected) => {
        const coordinates = await getCoordinatesFromPostalCode(selected.code);
        if (coordinates) {
            const newSelected = {
                ...selected,
                coordinates: coordinates,
            };
            setSelectedOption(newSelected);
            setPostalCode(newSelected);
            setShowPostalCodeError(false);
        } else {
            alert(t("Can not select this postal code at the moment, please another postalcode nearest to you."));
        }
    };

    const fetchLocationAndPostalCode = async () => {
        if ("geolocation" in navigator) {
            setLoading(true);
            navigator.geolocation.getCurrentPosition(async (position) => {
                const latitude = position.coords.latitude;
                const longitude = position.coords.longitude;
                console.log('Latitude:', latitude, 'Longitude:', longitude);

                const locationData = await fetchPostalCode(latitude, longitude);
                if (locationData) {
                    const newOption = {
                        ...locationData,
                        value: locationData.id,
                        label: locationData.city + "(" + locationData.code + `)`,
                        coordinates: { lat: latitude, lon: longitude },
                    };
                    setSelectedOption(newOption);
                    setPostalCode(newOption);
                } else {
                    alert(t("No postal code found for this location, please enter manually."));
                }

                setShowPostalCodeError(false);
                setLoading(false);
            }, (error) => {
                console.error("Error getting location:", error);
                setLoading(false);
            });
        } else {
            alert("Geolocation is not supported by your browser.");
        }
    };

    return (
        <div className="d-flex flex-column">
            <div className="form-group mt-4 mt-sm-0">
                {/*<label htmlFor="{{ form.postal_code.id_for_label }}">{ t("Where?") }</label>*/ }
                <div className="input-container p-0">
                    <AsyncPaginate
                        styles={ customStyles }
                        defaultOptions={ true }
                        value={ selectedOption }
                        loadOptions={ loadOptions }
                        closeMenuOnSelect={ true }
                        isClearable={ true }
                        hideSelectedOptions={ false }
                        onChange={ handleSelectedValue }
                        placeholder={ t("Address or Postal Code") }
                        loadingMessage={ () => t("Loading...") }
                        noOptionsMessage={ () => t("No result found") }
                        menuIsScrollable
                        // onMenuScrollToBottom={()=>{}}
                        debounceTimeout={ 1000 }
                    />
                    <div className="input-icon">
                        <div className="icon-wrapper icon-wrapper--location">
                            <img src="/static/img/ic_lieu.svg" alt={ t("location") }/>
                        </div>
                    </div>
                    {/*<button id="resetButton" type="reset">*/}
                    {/*    <img src="/static/img/ic_delete.svg" alt={ t("delete icon") }/>*/}
                    {/*</button>*/}
                    <button type="button" className="geolocation" disabled={ loading } onClick={ fetchLocationAndPostalCode }>
                        <img src="/static/img/ic_position.svg" alt={ t(" position") }/>
                        { loading
                            ?
                            <span className="d-none d-sm-inline-block">{ t("Loading...") }</span>
                            :
                            <span className="d-none d-sm-inline-block">{ t("Use my location") }</span>
                        }

                    </button>
                </div>
            </div>
            {
                showPostalCodeError
                    ?
                    <div className="postal-code-tooltip">{ t("Please fill in this field to continue the search") }</div>
                    : null
            }
            <a href={ currentLanguage === 'en' ? 'https://www.canadapost-postescanada.ca/cpc/en/tools/find-a-postal-code.page' : 'https://www.canadapost-postescanada.ca/scp/fr/outils/trouver-un-code-postal.page' } className="address-input-sub-label" target="_blank" rel="noopener noreferrer">{ t("Find a postal code") }</a>
        </div>
    );
};
PostalCode.prototype = {
    setPostalCode: PropTypes.func.isRequired,
    setShowPostalCodeError: PropTypes.func.isRequired,
    showPostalCodeError: PropTypes.bool.isRequired,
};


const OrganizationSearchStart = () => {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [service, setService] = useState(null);
    const [services, setServices] = useState(null);
    const [subServices, setSubServices] = useState([]);
    const [selectedSubServices, setSelectedSubServices] = useState([]);
    const [selectedPostalCode, setPostalCode] = useState(null);
    const [showPostalCodeError, setShowPostalCodeError] = useState(false);
    const handleServiceSelection = (service) => {
        setService(services.filter(s => s.name === service)[0]);
        if (services.filter(s => s.name === service)[0].sub_services.length) {
            setSubServices(services.filter(s => s.name === service)[0].sub_services);
            const selectedSubServices = services.filter(s => s.name === service)[0].sub_services.reduce((a, c) => {
                a[c.name] = true;
                return a;
            }, {});
            setSelectedSubServices(selectedSubServices);
            setStep(2);
        } else {
            fetchSubServices(service).then(response => {
                let subServices = response.results;
                subServices = subServices.map(s => ({...s, name:s.value}));
                setSubServices(subServices);
                const selectedSubServices = subServices.reduce((a, c) => {
                    a[c.name] = true;
                    return a;
                }, {});
                setSelectedSubServices(selectedSubServices);
                setStep(2);
            });
        }
    };
    const subServicesClickHandler = (s) => {
        setSelectedSubServices(p => {
            p[s] = !p[s];
            return { ...p };
        });
    };
    const getServices = async () => {
        const response = await fetchServices();
        setServices(response);
    };
    const formRef = useRef(null);
    const toggleSubServices = () => {
        if (Object.values(selectedSubServices).some(v => v === false)) {
            setSelectedSubServices(p => {
                for (let k in p) {
                    p[k] = true;
                }
                return { ...p };
            });
        } else {
            setSelectedSubServices(p => {
                for (let k in p) {
                    p[k] = false;
                }
                return { ...p };
            });
        }
    };
    const clearSubServices = () => {
        setSelectedSubServices(p => {
            for (let k in p) {
                p[k] = false;
            }
            return { ...p };
        });
    };
    const submitHandler = (e) => {
        //     redirect to redirectUrl
        e.preventDefault();
        // save postal code and  services and sub services in local storage
        if (selectedPostalCode === null) {
            setShowPostalCodeError(true);
            return;
        }
        // set query to empty string in local storage
        localStorage.setItem("query", "");
        localStorage.setItem("postalCode", JSON.stringify(selectedPostalCode));
        localStorage.setItem("service", JSON.stringify(service));
        localStorage.setItem("subServices", JSON.stringify(selectedSubServices));
        // eslint-disable-next-line no-undef
        window.location.href = redirectUrl;

    };
    useEffect(() => {
        getServices();
    }, []);
    return (
        <div className={ `search-service container ${ step === 3 ? "pre-submit" : "" }` }>
            <div className="search-service__title-container">
                <h2 className="search-service__title">{ t("Search by service") }</h2>
                { [2, 3].includes(step) &&
                    <button type="button" onClick={ () => setStep(s => {
                        return Math.max(1, s - 1);
                    }) } className="search-service__back">
                        <span className="material-icons">west</span>
                        <span>{ t("Back") }</span>
                    </button> }
            </div>
            <div className="search-service__indicators-container">
                <div className="row">
                    <div className="col-4 pr-2 d-flex align-items-end">
                        <span className={ `search-service__indicators ${ step === 1 ? "search-service__indicators--active" : "" }` }/>
                    </div>
                    <div className="col-4 px-2 d-flex align-items-end">
                        <span className={ `search-service__indicators ${ step === 2 ? "search-service__indicators--active" : "" }` }/>
                    </div>
                    <div className="col-4 pl-2 d-flex align-items-end">
                        <span className={ `search-service__indicators ${ step === 3 ? "search-service__indicators--active" : "" }` }/>
                    </div>
                </div>
            </div>
            {/*{ #        TODO: use this commented block for search by service buttons - first step # }*/ }
            {/*// <!--            START: Buttons container      -->*/ }
            { step === 1 && <div className="search-service__buttons-container">
                { services && <div className="row">
                    <div className="col-6 col-lg-3" onClick={ () => handleServiceSelection("respite_location") }>
                        <a className="search-service__buttons">
                            <img src="/static/img/ic_repit.svg" alt={ t(" respite icon") } aria-hidden="true"/>
                            <span>{ t("Respite") }</span>
                        </a>
                    </div>
                    <div className="col-6 col-lg-3" onClick={ () => handleServiceSelection("information") }>
                        <a className="search-service__buttons">
                            <img src="/static/img/ic_informations.svg" alt={ t(" information and documentation icon") } aria-hidden="true"/>
                            <span>{ t("Information And Documentation") }</span>
                        </a>
                    </div>
                    <div className="col-6 col-lg-3" onClick={ () => handleServiceSelection("trainings") }>
                        <a className="search-service__buttons">
                            <img src="/static/img/ic_activite_formation.svg" alt={ t(" training activities icon") } aria-hidden="true"/>
                            <span>{ t("Training Activities") }</span>
                        </a>
                    </div>
                    <div className="col-6 col-lg-3" onClick={ () => handleServiceSelection("support_groups") }>
                        <a className="search-service__buttons">
                            <img src="/static/img/ic_entraide.svg" alt={ t(" mutual aid and support icon") } aria-hidden="true"/>
                            <span>{ t("Mutual Aid And Support") }</span>
                        </a>
                    </div>

                    <div className="col-6 col-lg-3" onClick={ () => handleServiceSelection("administratives") }>
                        <a className="search-service__buttons">
                            <img src="/static/img/ic_juridique.svg" alt={ t(" administrative, legal & fiscal icon") } aria-hidden="true"/>
                            <span>{ t("Administrative, Legal & Fiscal") }</span>
                        </a>
                    </div>
                    <div className="col-6 col-lg-3" onClick={ () => handleServiceSelection("home_assists") }>
                        <a className="search-service__buttons">
                            <img src="/static/img/ic_aide_domicile.svg" alt={ t(" home assistance icon") } aria-hidden="true"/>
                            <span>{ t("Home Assistance") }</span>
                        </a>
                    </div>
                    <div className="col-6 col-lg-3" onClick={ () => handleServiceSelection("transportations") }>
                        <a className="search-service__buttons">
                            <img src="/static/img/ic_transport.svg" alt={ t(" transportation or accompaniment icon") } aria-hidden="true"/>
                            <span>{ t("Transportation / Accompaniment") }</span>
                        </a>
                    </div>
                    <div className="col-6 col-lg-3" onClick={ () => handleServiceSelection("accommodations") }>
                        <a className="search-service__buttons">
                            <img src="/static/img/ic_hebergement.svg" alt={ t(" lodging or accommodation icon") } aria-hidden="true"/>
                            <span>{ t("Lodging / Accommodation") }</span>
                        </a>
                    </div>
                </div> }
            </div>
            } {/*// <!--            END: Buttons container      -->*/ }
            {/*// <!--            START: Filters container      -->*/ }
            { (step === 2 || step === 3) && <div className="search-service__filters-container">
                <div className="search-service__filters-header">
                    <img src={ `/static/img/services/step-2/${ IMAGE[service.name] }` } alt={ service[getLKey("label")] } aria-hidden="true"/>
                    <span>{ service[getLKey("label_home")] }</span>
                </div>
                <form id="search-service__filters-form" className="form-grey" ref={ formRef }>
                    { step >= 2 && <div className="search-service__filters-group">
                        <button type="button" onClick={ toggleSubServices } className="search-service__filters search-service__filters--select-all">{ t("All resources") }</button>
                        <button type="button" onClick={ clearSubServices } className="search-service__filters search-service__filters--clear-all">{ t("Clear all") }</button>
                        { subServices && subServices.map(s => {
                                if (step === 3 && !!selectedSubServices[s.name] === false)
                                    return null;
                                return <button key={ s.name } type="button" className="search-service__filters form-grey-check" onClick={ () => subServicesClickHandler(s.name) }>
                                    <span>{ t("Precision") } :</span>
                                    <input className="form-check-input" type="checkbox" name="search-service__filters"
                                           onClick={ () => subServicesClickHandler(s.name) }
                                           checked={ !!selectedSubServices[s.name] } id={ s.name }/>
                                    <label className="form-check-label" htmlFor={ s.name }>
                                        { getLKey("label_home") in Object.keys(s)?s[getLKey("label_home")]:s[getLKey("label")] }
                                    </label>
                                    <img src="/static/img/ic_delete.svg" alt={ t(" Remove filter") }/>
                                </button>;
                            }
                        ) }
                    </div>
                    }
                    { step === 2 && <div className="text-center">
                        <button type="button" onClick={ () => setStep(3) }
                                className="btn-skin-1 search-service__filters--next">
                            <span className="material-icons" style={ { fontSize: "20px" } }>east</span>
                            <span>{ t("Next") }</span>
                        </button>
                    </div> }
                    {/*// <!--            START: Address input      -->*/ }
                    { step === 3 && <div className="mb-5 mb-sm-4">
                        <div className="form-group mt-4 mt-sm-0">
                            {/*<label htmlFor="">{ t("Where?") }</label>*/ }
                            <PostalCode setPostalCode={ setPostalCode }
                                        showPostalCodeError={ showPostalCodeError }
                                        setShowPostalCodeError={ setShowPostalCodeError }
                            />
                        </div>
                        {/*<p className="address-input-sub-label">{ t("Find a postal code") }</p>*/ }
                    </div>
                    } {/*// <!--            END: Address input      -->*/ }
                    { step === 3 && <div className="text-center">
                        <button type="button" onClick={ submitHandler } className="btn-skin-1 search-service__filters--submit">
                            <span className="material-icons" style={ { fontSize: "20px" } }>east</span>
                            <span>{ t("Search") }</span>
                        </button>
                    </div> }
                    {/*// <!-- Add other form elements like submit button if needed -->*/ }
                </form>
            </div> }
            {/*// <!--            END: Filters container      -->*/ }
        </div>
    );
};

const domNodeOrganizationSearch = document.querySelector("#organisationSearchStart");

if (domNodeOrganizationSearch) {
    // const rootOrgSearch = ReactDOM.createRoot(domNodeOrganizationSearch);
    // rootOrgSearch.render(<OrganizationSearch/>,domNodeOrganizationSearch);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(<OrganizationSearchStart/>, domNodeOrganizationSearch);
}